import React, { useEffect, useState } from "react";
import {
  FaEdit,
  FaPlus,
  FaCheckCircle,
  FaTimesCircle,
  FaFileAlt,
  FaSave,
  FaEye,
  FaBold,
  FaItalic,
  FaUnderline,
  FaStrikethrough,
  FaListUl,
  FaListOl,
  FaQuoteLeft,
  FaCode,
  FaLink,
  FaHeading,
  FaEraser,
} from "react-icons/fa";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { contentGetAll, contentUpdate, contentAdd } from "../../api/endpoints";
import "./ContentList.css";
import PageHeader from "../../components/PageHeader";

const ALL_TYPES = ["PRIVACY_POLICY", "TERMS_CONDITIONS", "ABOUT_US", "CONTACT_US", "AVIATOR_NOTICE", "DROGANTIGER_NOTICE"];

// ============================================
// TipTap Toolbar Component
// ============================================
const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  return (
    <div className="tiptap-toolbar">
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
        title="Heading 1"
      >
        <FaHeading /> H1
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
        title="Heading 2"
      >
        <FaHeading /> H2
      </button>
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'is-active' : ''}
        title="Bold"
      >
        <FaBold />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'is-active' : ''}
        title="Italic"
      >
        <FaItalic />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={editor.isActive('strike') ? 'is-active' : ''}
        title="Strikethrough"
      >
        <FaStrikethrough />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'is-active' : ''}
        title="Bullet List"
      >
        <FaListUl />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'is-active' : ''}
        title="Numbered List"
      >
        <FaListOl />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={editor.isActive('blockquote') ? 'is-active' : ''}
        title="Blockquote"
      >
        <FaQuoteLeft />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={editor.isActive('codeBlock') ? 'is-active' : ''}
        title="Code Block"
      >
        <FaCode />
      </button>
      <button
        onClick={() => {
          const url = window.prompt('Enter URL');
          if (url) {
            editor.chain().focus().setLink({ href: url }).run();
          }
        }}
        className={editor.isActive('link') ? 'is-active' : ''}
        title="Add Link"
      >
        <FaLink />
      </button>
      <button
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive('link')}
        title="Remove Link"
      >
        <FaLink style={{ textDecoration: 'line-through' }} />
      </button>
      <button
        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        title="Clear Formatting"
      >
        <FaEraser />
      </button>
    </div>
  );
};

// ============================================
// TipTap Editor Wrapper Component
// ============================================
const TipTapEditor = ({ value, onChange, placeholder }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure(),
      Placeholder.configure({
        placeholder: placeholder || "Write your content here...",
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
  });

  // Update editor content when value changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  return (
    <div className="tiptap-editor-wrapper">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} className="tiptap-content" />
    </div>
  );
};

// ============================================
// Main ContentList Component
// ============================================
const ContentList = () => {
  const [contentList, setContentList] = useState({});
  const [loading, setLoading] = useState(false);

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({
    type: "",
    title: "",
    content: "",
    isActive: true,
  });
  const [saving, setSaving] = useState(false);

  // Add Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addData, setAddData] = useState({
    type: "PRIVACY_POLICY",
    title: "",
    content: "",
    isActive: true,
  });
  const [adding, setAdding] = useState(false);

  // View Modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupTitle, setPopupTitle] = useState("");

  // ============================================
  // Helper Functions
  // ============================================
  const getTypeLabel = (type) => {
    const types = {
      PRIVACY_POLICY: "Privacy Policy",
      TERMS_CONDITIONS: "Terms & Conditions",
      ABOUT_US: "About Us",
      CONTACT_US: "Contact Us",
      AVIATOR_NOTICE: "Aviator Notice",
      DROGANTIGER_NOTICE: "Drogantiger Notice",
    };
    return types[type] || type;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Get all content items as flat array
  const getAllContentItems = () => {
    return Object.values(contentList).flat();
  };

  // Check if a type already has content
  const isTypeExists = (type) => {
    return contentList[type] && contentList[type].length > 0;
  };

  // Get available types for adding
  const getAvailableTypes = () => {
    return ALL_TYPES.filter(type => !isTypeExists(type));
  };

  // ============================================
  // Popup Helpers
  // ============================================
  const showPopup = (title, message, type = "success") => {
    setPopupTitle(title);
    setPopupMessage(message);
    if (type === "success") {
      setShowSuccessPopup(true);
      setShowErrorPopup(false);
    } else {
      setShowErrorPopup(true);
      setShowSuccessPopup(false);
    }
    setTimeout(() => closePopup(), 5000);
  };

  const closePopup = () => {
    setShowSuccessPopup(false);
    setShowErrorPopup(false);
  };

  // ============================================
  // Fetch Content
  // ============================================
  const fetchContentList = async () => {
    try {
      setLoading(true);
      const res = await contentGetAll();
      if (res.success) {
        const dataObj = res.data || {};
        
        // Initialize with empty arrays for all types
        const groupedData = {};
        ALL_TYPES.forEach(type => {
          groupedData[type] = dataObj[type] || [];
        });
        
        setContentList(groupedData);
      } else {
        showPopup("Error", res.message || "Failed to fetch content", "error");
      }
    } catch (err) {
      console.error("Error fetching content list:", err);
      showPopup(
        "Error",
        err?.response?.data?.message || "Failed to fetch content list",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initialize with empty arrays for all types
    const initialData = {};
    ALL_TYPES.forEach(type => {
      initialData[type] = [];
    });
    setContentList(initialData);
    fetchContentList();
  }, []);

  // ============================================
  // Add Modal
  // ============================================
  const openAddModal = () => {
    const availableTypes = getAvailableTypes();
    const firstAvailableType = availableTypes.length > 0 ? availableTypes[0] : ALL_TYPES[0];

    setAddData({
      type: firstAvailableType,
      title: "",
      content: "",
      isActive: true,
    });
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
  };

  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    setAddData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddSubmit = async () => {
    if (!addData.title.trim()) {
      showPopup("Validation Error", "Please enter title", "error");
      return;
    }
    if (!addData.content.trim()) {
      showPopup("Validation Error", "Please enter content", "error");
      return;
    }

    try {
      setAdding(true);

      const payload = {
        type: addData.type,
        title: addData.title,
        content: addData.content,
        isActive: addData.isActive,
      };

      console.log("📤 Adding content with type:", addData.type);
      console.log("📤 Payload:", payload);

      const res = await contentAdd(payload);

      if (res.success) {
        showPopup("Success", res.message || "Content added successfully!", "success");
        closeAddModal();
        await fetchContentList(); // Refresh the list
      } else {
        showPopup("Error", res.message || "Failed to add content", "error");
      }
    } catch (err) {
      console.error("Error adding content:", err);
      showPopup(
        "Error",
        err?.response?.data?.message || "Something went wrong",
        "error"
      );
    } finally {
      setAdding(false);
    }
  };

  // ============================================
  // Edit Modal
  // ============================================
  const openEditModal = (item) => {
    setEditData({
      type: item.type,
      title: item.title,
      content: item.content,
      isActive: item.isActive,
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditData({ type: "", title: "", content: "", isActive: true });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async () => {
    if (!editData.title.trim()) {
      showPopup("Validation Error", "Please enter title", "error");
      return;
    }
    if (!editData.content.trim()) {
      showPopup("Validation Error", "Please enter content", "error");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        title: editData.title,
        content: editData.content,
        isActive: editData.isActive,
      };

      console.log("📤 Updating content for type:", editData.type);
      console.log("📤 Payload:", payload);

      const res = await contentUpdate(editData.type, payload);

      if (res.success) {
        showPopup("Success", res.message || "Content updated successfully!", "success");
        closeEditModal();
        await fetchContentList(); // Refresh the list
      } else {
        showPopup("Error", res.message || "Failed to update content", "error");
      }
    } catch (err) {
      console.error("Error updating content:", err);
      showPopup(
        "Error",
        err?.response?.data?.message || "Something went wrong",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // View Modal
  // ============================================
  const openViewModal = (item) => {
    setViewData(item);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewData(null);
  };

  // Get all items for display
  const allItems = getAllContentItems();
  const availableTypes = getAvailableTypes();

  return (
    <div className="content-list-page lead-page">
      <div className="row">
        <div className="col-lg-12">
          <PageHeader
            title="Content Management"
            subtitle="Manage Privacy Policy, Terms & Conditions, About Us, Contact Us, Aviator Notice, and Drogantiger Notice content"
            actions={
              <>
            <button
                  className="btn btn-dark"
                  onClick={openAddModal}
                  disabled={availableTypes.length === 0}
                >
                  <FaPlus className="me-2" />
                  Add Content
                </button>
              </>
            }
          />

          <div className="card border-0 shadow-sm rounded-4 mt-3">
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : allItems.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <FaFileAlt size={40} className="mb-3" />
                  <p>No content found. Please add content first.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Type</th>
                        <th>Title</th>
                        <th>Content</th>
                        <th>Status</th>
                        <th>Last Updated</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ALL_TYPES.map((type) => {
                        const items = contentList[type] || [];
                        if (items.length === 0) return null;
                        
                        return items.map((item, index) => (
                          <tr key={item._id || item.type}>
                            <td>{index + 1}</td>
                            <td>
                              <span className="badge bg-primary-subtle text-primary">
                                {getTypeLabel(item.type)}
                              </span>
                            </td>
                            <td>{item.title}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => openViewModal(item)}
                              >
                                <FaEye className="me-1" />
                                Preview
                              </button>
                            </td>
                            <td>
                              {item.isActive ? (
                                <span className="badge bg-success">
                                  <FaCheckCircle className="me-1" />
                                  Active
                                </span>
                              ) : (
                                <span className="badge bg-danger">
                                  <FaTimesCircle className="me-1" />
                                  Inactive
                                </span>
                              )}
                            </td>
                            <td>{formatDate(item.updatedAt)}</td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => openEditModal(item)}
                              >
                                <FaEdit className="me-1" />
                                Edit
                              </button>
                            </td>
                          </tr>
                        ));
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* ADD MODAL */}
      {/* ============================================ */}
      {showAddModal && (
        <div className="popup-overlay" onClick={closeAddModal}>
          <div
            className="popup-container edit-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ position: "relative", maxWidth: "800px", width: "95%" }}
          >
            <button
              onClick={closeAddModal}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "#f1f1f1",
                border: "none",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                fontSize: "18px",
                fontWeight: "bold",
                color: "#555",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: "1",
                zIndex: 10,
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = "#e0e0e0")}
              onMouseOut={(e) => (e.currentTarget.style.background = "#f1f1f1")}
            >
              &times;
            </button>

            <div className="mb-3 pe-4">
              <h4 className="mb-0">Add New Content</h4>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">
                Type <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                name="type"
                value={addData.type}
                onChange={handleAddInputChange}
              >
                {ALL_TYPES.map((t) => {
                  const existing = isTypeExists(t);
                  return (
                    <option
                      key={t}
                      value={t}
                      disabled={existing}
                    >
                      {getTypeLabel(t)}
                      {existing ? " (already exists)" : ""}
                    </option>
                  );
                })}
              </select>
              {availableTypes.length === 0 && (
                <small className="text-muted d-block mt-1">
                  All content types have been added already.
                </small>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">
                Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                name="title"
                value={addData.title}
                onChange={handleAddInputChange}
                placeholder="Enter title"
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Status</label>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="addStatusSwitch"
                  checked={addData.isActive}
                  onChange={(e) =>
                    setAddData((prev) => ({ ...prev, isActive: e.target.checked }))
                  }
                  style={{ width: "3em", height: "1.5em" }}
                />
                <label className="form-check-label ms-2" htmlFor="addStatusSwitch">
                  <span
                    className={`badge ${addData.isActive ? "bg-success" : "bg-danger"}`}
                  >
                    {addData.isActive ? "Active" : "Inactive"}
                  </span>
                </label>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">
                Content <span className="text-danger">*</span>
              </label>
              <div style={{
                border: "1px solid #ddd",
                borderRadius: "4px",
                background: "white"
              }}>
                <TipTapEditor
                  value={addData.content}
                  onChange={(value) => setAddData((prev) => ({ ...prev, content: value }))}
                  placeholder="Write your content here..."
                />
              </div>
            </div>

            <div className="d-flex gap-2 justify-content-end mt-3">
              <button
                className="btn btn-secondary"
                onClick={closeAddModal}
                disabled={adding}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddSubmit}
                disabled={adding || availableTypes.length === 0}
              >
                <FaSave className="me-2" />
                {adding ? "Adding..." : "Add Content"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* EDIT MODAL */}
      {/* ============================================ */}
      {showEditModal && (
        <div className="popup-overlay" onClick={closeEditModal}>
          <div
            className="popup-container edit-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ position: "relative", maxWidth: "800px", width: "95%" }}
          >
            <button
              onClick={closeEditModal}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "#f1f1f1",
                border: "none",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                fontSize: "18px",
                fontWeight: "bold",
                color: "#555",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: "1",
                zIndex: 10,
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = "#e0e0e0")}
              onMouseOut={(e) => (e.currentTarget.style.background = "#f1f1f1")}
            >
              &times;
            </button>

            <div className="mb-3 pe-4">
              <h4 className="mb-0">Edit {getTypeLabel(editData.type)}</h4>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">
                Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                name="title"
                value={editData.title}
                onChange={handleEditInputChange}
                placeholder="Enter title"
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Status</label>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="editStatusSwitch"
                  checked={editData.isActive}
                  onChange={(e) =>
                    setEditData((prev) => ({ ...prev, isActive: e.target.checked }))
                  }
                  style={{ width: "3em", height: "1.5em" }}
                />
                <label className="form-check-label ms-2" htmlFor="editStatusSwitch">
                  <span
                    className={`badge ${editData.isActive ? "bg-success" : "bg-danger"}`}
                  >
                    {editData.isActive ? "Active" : "Inactive"}
                  </span>
                </label>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">
                Content <span className="text-danger">*</span>
              </label>
              <div style={{
                border: "1px solid #ddd",
                borderRadius: "4px",
                background: "white"
              }}>
                <TipTapEditor
                  value={editData.content}
                  onChange={(value) => setEditData((prev) => ({ ...prev, content: value }))}
                  placeholder="Write your content here..."
                />
              </div>
            </div>

            <div className="d-flex gap-2 justify-content-end mt-3">
              <button
                className="btn btn-secondary"
                onClick={closeEditModal}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdate}
                disabled={saving}
              >
                <FaSave className="me-2" />
                {saving ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* VIEW MODAL */}
      {/* ============================================ */}
      {showViewModal && viewData && (
        <div className="popup-overlay" onClick={closeViewModal}>
          <div
            className="popup-container edit-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ position: "relative", maxWidth: "700px", width: "95%" }}
          >
            <button
              onClick={closeViewModal}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "#f1f1f1",
                border: "none",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                fontSize: "18px",
                fontWeight: "bold",
                color: "#555",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: "1",
                zIndex: 10,
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = "#e0e0e0")}
              onMouseOut={(e) => (e.currentTarget.style.background = "#f1f1f1")}
            >
              &times;
            </button>

            <div className="d-flex justify-content-between align-items-center mb-3 pe-4">
              <h4 className="mb-0">{viewData.title}</h4>
              <span className="badge bg-secondary">{getTypeLabel(viewData.type)}</span>
            </div>

            <div
              style={{
                maxHeight: "60vh",
                overflowY: "auto",
                padding: "20px",
                background: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
              }}
              dangerouslySetInnerHTML={{ __html: viewData.content }}
            />
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* SUCCESS POPUP */}
      {/* ============================================ */}
      {showSuccessPopup && (
        <div className="popup-overlay" onClick={closePopup}>
          <div
            className="popup-container popup-success"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popup-icon">
              <FaCheckCircle className="text-success" size={48} />
            </div>
            <h3 className="popup-title">{popupTitle}</h3>
            <p className="popup-message">{popupMessage}</p>
            <button className="popup-btn btn-success" onClick={closePopup}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* ERROR POPUP */}
      {/* ============================================ */}
      {showErrorPopup && (
        <div className="popup-overlay" onClick={closePopup}>
          <div
            className="popup-container popup-error"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popup-icon">
              <FaTimesCircle className="text-danger" size={48} />
            </div>
            <h3 className="popup-title">{popupTitle}</h3>
            <p className="popup-message">{popupMessage}</p>
            <button className="popup-btn btn-danger" onClick={closePopup}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentList;